import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalHeader, ModalBody, Row, Col } from "reactstrap";
import TableComponent from "../../components/TableComponent";
import Toast from "../../helpers/Toast";
import { ClientAddressService } from "../../services/ClientAddressService";
import { CountryService } from "../../services/CountryService";
import { EquipmentDTO } from "../../models/Equipment/EquipmentDTO";
import { ClientAddressDTO } from "../../models/ClientAddress/ClientAdressDTO";
import ButtonComponent from "../ButtonComponent";

type Props = {
  visible: boolean;
  onClose: () => void;
  equipment: EquipmentDTO;
  onSelectAddress: (address: ClientAddressDTO) => void; 
};

export default function ClientAddressPopup({ visible, onClose, equipment, onSelectAddress }: Props) {
  const intl = useIntl();

  const addressService = new ClientAddressService();
  const countryService = new CountryService();

  const [data, setData] = useState<ClientAddressDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const clientId = equipment?.client?.id;

  const getCountryName = async (countryId: number): Promise<string> => {
    try {
      const result = await countryService.GetCountryById(countryId);
      if (result.success && result.obj) {
        return result.obj.name || `País ID: ${countryId}`;
      }
      return `País ID: ${countryId}`;
    } catch {
      return `País ID: ${countryId}`;
    }
  };

  const fetchData = async (page: number, size: number) => {
    if (!clientId) {
      setData([]);
      setPageCount(0);
      return;
    }

    setLoading(true);
    try {
      const result = await addressService.getAllActiveByClientId(clientId);

      if (!result.success || !Array.isArray(result.obj)) {
        Toast.Show("error", intl.formatMessage({ id: "Erro ao carregar endereços." }));
        setData([]);
        setPageCount(0);
      } else {
        const allData = result.obj;

        const enrichedData = await Promise.all(
          allData.map(async (item: ClientAddressDTO) => {
            const countryName = item.countryId ? await getCountryName(item.countryId) : "----";
            return { ...item, countryName };
          })
        );

        const start = (page - 1) * size;
        const paginated = enrichedData.slice(start, start + size);
        setData(paginated);
        setPageCount(Math.ceil(enrichedData.length / size));
      }
    } catch {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.LoadError" }));
      setData([]);
      setPageCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      fetchData(currentPage, pageSize);
    }
  }, [visible, clientId, currentPage, pageSize]);

  const handleSelectAddress = (address: ClientAddressDTO) => {
    onSelectAddress(address);
    onClose();               
  };

  const columns = [
    {
      Header: intl.formatMessage({ id: "Screens.Client.Info.Street" }),
      accessor: "street1",
      width: 30,
    },
    {
      Header: intl.formatMessage({ id: "Screens.Client.Info.Street2" }),
      accessor: "street2",
      width: 30,
    },
    {
      Header: intl.formatMessage({ id: "Screens.Client.Info.PostalCode" }),
      accessor: "postalCode",
      width: 15,
    },
    {
      Header: intl.formatMessage({ id: "Screens.Client.Info.City" }),
      accessor: "city",
      width: 20,
    },
    {
      Header: intl.formatMessage({ id: "Screens.Client.Info.NameCountry" }),
      accessor: "countryName",
      width: 25,
    },
    {
      Header: intl.formatMessage({ id: "Screens.Common.Buttons.Select" }),
      id: "select",
      width: 15,
      Cell: ({ row }: any) => (
        <ButtonComponent
          text={intl.formatMessage({ id: "Screens.Common.Buttons.Select" })}
          onClick={() => handleSelectAddress(row.original)}
          style={{ minWidth: "80px" }}
        />
      ),
    },
  ];

  return (
    <Modal isOpen={visible} toggle={onClose} size="lg" style={{ maxWidth: "900px" }}>
      <ModalHeader style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <h5 style={{ marginBottom: "0.5rem" }}>
          {intl.formatMessage({ id: "Screens.ClientAddresses.Title" })}
        </h5>
        <ButtonComponent
          text={intl.formatMessage({ id: "Screens.EquipmentEdit.Back" })}
          onClick={onClose}
          style={{ width: "auto" }}
        />
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col xs="12" style={{ height: "400px", overflowY: "auto" }}>
            <TableComponent
              columns={columns}
              data={data}
              fetchData={fetchData}
              loading={loading}
              manualPagination={true}
              pageCount={pageCount}
              initialPage={currentPage}
              initialPageSize={pageSize}
              pageSizes={[5, 10, 20]}
              goToPage={undefined}
              initialSortBy={[]}
            />
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
}
